import { Box, Card, CardContent, Divider, Grid, Typography, MenuItem, Menu, Checkbox, Autocomplete, Stack, TextField, Paper } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LineChart } from '@mui/x-charts/LineChart';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StatCards from 'components/Widgets/StatCards/StatCards';
import { DataGrid, GridFooterContainer } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { search } from 'store/application/application-dashboard';
import { alpha } from '@mui/material/styles';
import { stringToColor } from 'components/Avatar';
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

const WEEKS = 8;

const Panel = ({ title, action, children, sx }) => (
    <Card
        elevation={0}
        sx={{
            width: '100%',
            height: '100%',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            ...sx,
        }}
    >
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}
        >
            <Typography variant="subtitle1" fontWeight={700} noWrap>
                {title}
            </Typography>
            {action}
        </Stack>
        <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>{children}</CardContent>
    </Card>
);

const seasonEndFor = (startDate) => dayjs(new Date(dayjs(startDate).year(), 5, 30)).endOf('day');

const generateWeeklyApplicationData = (applicationSchools, year, isActiveSeason) => {
    const anchor = isActiveSeason || !year?.startDate ? dayjs() : seasonEndFor(year.startDate);
    const firstWeek = anchor.startOf('isoWeek').subtract(WEEKS - 1, 'week');
    const starts = Array.from({ length: WEEKS }, (_, index) => firstWeek.add(index, 'week'));
    const keys = starts.map((week) => week.format('YYYY-MM-DD'));

    const dataBySchool = {};

    (applicationSchools ?? []).forEach((school) => {
        const counts = Object.fromEntries(keys.map((key) => [key, 0]));

        school.applications?.forEach((application) => {
            const key = dayjs(application.createdAt).startOf('isoWeek').format('YYYY-MM-DD');
            if (key in counts) counts[key] += 1;
        });

        dataBySchool[school.abbreviation.toLowerCase()] = keys.map((key) => counts[key]);
    });

    return { weeks: starts.map((week) => week.format('MMM D')), dataBySchool };
};

export default function ApplicationsDashboard({ academicYears }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const applicationSchools = useSelector((state) => state.application.applicationDashboard.list);
    const applicationStats = useSelector((state) => state.application.applicationDashboard.stats);
    const [visibleCampuses, setVisibleCampuses] = useState([]);
    const [schools, setSchools] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);

    const activeYearId = useMemo(() => {
        if (!academicYears?.length) return null;

        return academicYears.reduce((newest, year) =>
            (dayjs(year.startDate).isAfter(newest.startDate) ? year : newest)).id;
    }, [academicYears]);

    const { weeks, dataBySchool } = generateWeeklyApplicationData(
        applicationSchools,
        academicYears?.find((option) => option.id === selectedAcademicYear),
        selectedAcademicYear != null && selectedAcademicYear === activeYearId,
    );

    useEffect(() => {
        if (selectedAcademicYear || !activeYearId) return;
        setSelectedAcademicYear(activeYearId);
    }, [activeYearId, selectedAcademicYear]);

    useEffect(() => {
        if (!selectedAcademicYear) return;
        dispatch(search({ academicYearProviderId: selectedAcademicYear }));
    }, [dispatch, selectedAcademicYear]);


    useEffect(() => {
        if (!applicationSchools?.length) return;
        const names = applicationSchools.map(s => s.abbreviation);
        setSchools(names);
        setVisibleCampuses(prev =>
            prev.length === names.length && prev.every(c => names.includes(c)) ? prev : names
        );
    }, [applicationSchools]);

    const toggleCampusVisibility = (campus) => {
        setVisibleCampuses(prev =>
            prev.includes(campus)
                ? prev.filter(c => c !== campus)
                : [...prev, campus]
        );
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const campusData = (applicationSchools ?? [])
        .map((school) => ({
            campus: school.abbreviation,
            applications: Array.isArray(school.applications) ? school.applications.length : 0,
            color: stringToColor(school.abbreviation),
        }))
        .sort((a, b) => b.applications - a.applications);

    const cards = [
        {
            label: 'Applicants',
            value: applicationStats?.applicants ?? 0,
            delta: applicationStats?.applicantsSinceLastWeek ?? null,
            caption: 'since last week',
            icon: 'students',
        },
        {
            label: 'Total Applications',
            value: applicationStats?.applications ?? 0,
            delta: applicationStats?.applicationsSinceLastWeek ?? null,
            caption: 'since last week',
            icon: 'applications',
        },
        {
            label: 'Single School',
            value: applicationStats?.singleSchool ?? 0,
            delta: applicationStats?.singleSchoolSinceLastWeek ?? null,
            caption: 'since last week',
            icon: 'school',
        },
        {
            label: 'Multi-School',
            value: applicationStats?.multiSchool ?? 0,
            delta: applicationStats?.multiSchoolSinceLastWeek ?? null,
            caption: 'since last week',
            icon: 'multiSchool',
        },
    ];

    const rows = (() => {
        if (!applicationSchools || !Array.isArray(applicationSchools)) return [];
        const gradeSet = new Set();
        applicationSchools.forEach(school => {
            if (school.applicationStatsByGrade) {
                Object.keys(school.applicationStatsByGrade).forEach(grade => gradeSet.add(grade));
            }
        });

        // Sort Grades
        const grades = Array.from(gradeSet);
        const gradeOrder = (grade) => {
            if (grade.toLowerCase() === 'prek' || grade.toLowerCase() === 'pre-k') return 0;
            if (grade.toLowerCase() === 'k' || grade === '0') return 1;
            const num = parseInt(grade, 10);
            if (!isNaN(num)) return num + 1;
            return 100;
        };
        grades.sort((a, b) => gradeOrder(a) - gradeOrder(b));

        return grades.map((grade, idx) => {
            const row = { id: idx + 1, grade };
            applicationSchools.forEach(school => {
                row[school.abbreviation] = school.applicationStatsByGrade?.[grade] ?? 0;
            });
            return row;
        });
    })();

    const columns = [
        { field: 'grade', headerName: t('Grade'), width: 90, sortable: false },
        ...schools.map((school) => ({
            field: school,
            headerName: school,
            flex: 1,
            minWidth: 110,
            align: 'right',
            headerAlign: 'right',
            sortable: false,
            // the same dot the chart legend uses, so a column is identifiable at a glance
            renderHeader: () => (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stringToColor(school) }} />
                    <Box component="span" sx={{ fontWeight: 700 }}>{school}</Box>
                </Stack>
            ),
            // a zero is noise next to real counts; the eye should land on grades that have applicants
            renderCell: ({ value }) => (
                <Box component="span" sx={{ color: value ? 'text.primary' : 'text.disabled' }}>
                    {value ? Number(value).toLocaleString() : '—'}
                </Box>
            ),
        })),
    ];

    const totals = {};
    schools.forEach((school) => {
        totals[school] = rows.reduce((acc, row) => acc + row[school], 0);
    });

    const overallTotal = Object.values(totals).reduce((acc, n) => acc + n, 0);

    function CustomFooter() {
        return (
            <GridFooterContainer
                sx={{
                    display: 'block',
                    borderTop: '2px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    p: 0,
                }}
            >
                <Stack direction="row" alignItems="center" sx={{ px: 1.25, py: 1 }}>
                    <Box sx={{ width: 90, flexShrink: 0, fontWeight: 700, fontSize: 14 }}>{t('Totals')}</Box>
                    {schools.map((school) => (
                        <Box
                            key={school}
                            sx={{
                                flex: 1,
                                minWidth: 110,
                                textAlign: 'right',
                                pr: 1.25,
                                fontWeight: 700,
                                fontSize: 14,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {totals[school].toLocaleString()}
                        </Box>
                    ))}
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={theme => ({
                        px: 1.25,
                        py: 1.25,
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.07),
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    })}
                >
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t('iLearn Total')}</Typography>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: 20,
                            color: 'primary.main',
                            fontVariantNumeric: 'tabular-nums',
                            pr: 1.25,
                        }}
                    >
                        {overallTotal.toLocaleString()}
                    </Typography>
                </Stack>
            </GridFooterContainer>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', pt: 1 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 1, p: 2 }}
                component={Paper}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {t('Admissions Overview')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Applications across every school for the selected year.')}
                    </Typography>
                </Box>

                    <Autocomplete
                        options={academicYears}
                        getOptionLabel={(option) => option.label}
                        value={academicYears.find(opt => opt.id === selectedAcademicYear) || null}
                        onChange={(event, newValue) => {
                            setSelectedAcademicYear(newValue ? newValue.id : null);
                        }}
                        sx={{ width: { xs: '100%', sm: 280 } }}
                        renderInput={(params) => (
                            <TextField
                                variant="outlined"
                                {...params}
                                label={t('Select Academic Year')}
                                fullWidth
                                size="small"
                            />
                        )}
                    />
            </Stack>
            <StatCards cards={cards} variant="metric" columns={3} isReady={Boolean(applicationStats)} />
            <Grid container spacing={2} sx={{ mt: -1 }}>
                <Grid item xs={12}>
                    <Panel title={t('Applications Summary')}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            hideFooterPagination
                            slots={{ footer: CustomFooter }}
                            density="compact"
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                '& .MuiDataGrid-cell': { borderColor: 'divider' },
                                '& .MuiDataGrid-cell--textRight, & .MuiDataGrid-columnHeader--alignRight': {
                                    fontVariantNumeric: 'tabular-nums',
                                },
                                '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
                            }}
                        />
                    </Panel>

                </Grid>
            </Grid>

            {/* Campus Bar Chart & Line Chart */}
            <Grid container spacing={2} sx={{ mt: -1, pb: 2 }}>
                <Grid item xs={12} md={6}>
                    <Panel title={t('Applications By School')}>
                            <BarChart
                                dataset={campusData}
                                xAxis={[
                                    {
                                        scaleType: 'band',
                                        dataKey: 'campus',
                                        disableLine: true,
                                        disableTicks: true,
                                        categoryGapRatio: 0.3,
                                        colorMap: {
                                            type: 'ordinal',
                                            values: campusData.map(entry => entry.campus),
                                            colors: campusData.map(entry => entry.color),
                                        },
                                    },
                                ]}
                                yAxis={[{ disableLine: true, disableTicks: true }]}
                                series={[{ dataKey: 'applications' }]}
                                barLabel={(item, context) => (context.bar.height >= 28 ? String(item.value ?? '') : null)}
                                grid={{ horizontal: true }}
                                height={360}
                                borderRadius={4}
                                margin={{ top: 16, right: 8, bottom: 40, left: 44 }}
                                sx={theme => ({
                                    '& .MuiChartsGrid-line': { strokeDasharray: '3 4', opacity: 0.45 },
                                    '& .MuiBarLabel-root': {
                                        fontSize: 12,
                                        fontWeight: 700,
                                        fill: theme.palette.text.primary,
                                        stroke: theme.palette.background.paper,
                                        strokeWidth: 3,
                                        paintOrder: 'stroke',
                                    },
                                    '& .MuiChartsAxis-tickLabel': { fontSize: 12 },
                                })}
                            />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Panel
                        title={t('Applications Over Time')}
                    >
                            <Menu
                                    anchorEl={anchorEl}
                                    open={openMenu}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    PaperProps={{
                                        sx: {
                                            mt: 0,
                                            px: 0,
                                            py: 0,
                                            minWidth: 180,
                                        },
                                    }}
                                >
                                    <MenuItem
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 0.3,
                                            px: 1,
                                            gap: 0.5,
                                            minHeight: 32,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                checked={visibleCampuses.length === schools.length}
                                                indeterminate={visibleCampuses.length > 0 && visibleCampuses.length < schools.length}
                                                onChange={() => {
                                                    if (visibleCampuses.length === schools.length) {
                                                        setVisibleCampuses([]);
                                                    } else {
                                                        setVisibleCampuses([...schools]);
                                                    }
                                                }}
                                                size='small'
                                            />
                                            <Typography>All</Typography>
                                        </Box>
                                    </MenuItem>
                                    <Divider />
                                    {schools.map((campus) => (
                                        <MenuItem
                                            key={campus}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                py: 0.3,
                                                px: 1,
                                                gap: 0.5,
                                                minHeight: 32,
                                            }}

                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Checkbox
                                                    checked={visibleCampuses.includes(campus)}
                                                    onChange={() => toggleCampusVisibility(campus)}
                                                    size='small'
                                                />
                                                <Typography>{campus}</Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                            </Menu>

                            <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, pb: 1.5 }}>
                                {schools.map((school) => {
                                    const shown = visibleCampuses.includes(school);
                                    const color = stringToColor(school);

                                    return (
                                        <Box
                                            key={school}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => toggleCampusVisibility(school)}
                                            onKeyDown={(event) =>
                                                ['Enter', ' '].includes(event.key) &&
                                                (event.preventDefault(), toggleCampusVisibility(school))
                                            }
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                border: '1px solid',
                                                borderColor: shown ? alpha(color, 0.5) : 'divider',
                                                bgcolor: shown ? alpha(color, 0.1) : 'transparent',
                                                opacity: shown ? 1 : 0.5,
                                                transition: 'opacity .15s, background-color .15s',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    flexShrink: 0,
                                                    bgcolor: color,
                                                }}
                                            />
                                            <Typography variant="caption" fontWeight={shown ? 700 : 500}>
                                                {school}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>

                            <LineChart
                                xAxis={[
                                    {
                                        data: weeks,
                                        scaleType: 'point',
                                        disableLine: true,
                                        disableTicks: true,
                                    },
                                ]}
                                sx={{
                                    '& .MuiChartsGrid-line': { strokeDasharray: '3 4', opacity: 0.45 },
                                    '& .MuiLineElement-root': { strokeWidth: 2.5 },
                                    '& .MuiMarkElement-root': { strokeWidth: 2, scale: '0.7' },
                                    '& .MuiChartsAxis-tickLabel': { fontSize: 12 },
                                }}
                                yAxis={[{ disableLine: true, disableTicks: true, min: 0 }]}
                                height={360}
                                margin={{ top: 16, right: 32, bottom: 40, left: 44 }}
                                slotProps={{ legend: { hidden: true } }}
                                grid={{ horizontal: true }}
                                series={visibleCampuses.map((campus) => ({
                                    data: dataBySchool[campus.toLowerCase()] || [],
                                    label: campus,
                                    color: stringToColor(campus),
                                    curve: 'monotoneX',
                                    showMark: true,
                                }))}
                            />
                    </Panel>
                </Grid>
            </Grid>

        </Box>
    );
}
